import {
    type ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react"

import { type OwnTeamDto, getJoinedTeam, syncTeam } from "@/services/core"
import { useRoleInfo } from "@/user/session"

interface TeamContextValue {
    ownTeam: OwnTeamDto | undefined
    joinedTeam: OwnTeamDto | null | undefined
    mutateOwnTeam: () => void
    mutateJoinedTeam: () => void
}

const TeamContext = createContext<TeamContextValue>({
    ownTeam: undefined,
    joinedTeam: undefined,
    mutateOwnTeam: () => {},
    mutateJoinedTeam: () => {},
})

export const TeamProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const user = useRoleInfo()

    const [ownTeam, setOwnTeam] = useState<OwnTeamDto | undefined>(undefined)
    const [joinedTeam, setJoinedTeam] = useState<OwnTeamDto | null | undefined>(undefined)
    const [ownRefreshKey, setOwnRefreshKey] = useState(0)
    const [joinedRefreshKey, setJoinedRefreshKey] = useState(0)

    useEffect(() => {
        if (!user) {
            setOwnTeam(undefined)
            return
        }
        syncTeam().then((res) => {
            if (res.code === 0) setOwnTeam(res.data)
        })
    }, [user, ownRefreshKey])

    useEffect(() => {
        if (!user) {
            setJoinedTeam(undefined)
            return
        }
        getJoinedTeam().then((res) => {
            if (res.code === 0) setJoinedTeam(res.data)
        })
    }, [user, joinedRefreshKey])

    const mutateOwnTeam = useCallback(() => setOwnRefreshKey((k) => k + 1), [])
    const mutateJoinedTeam = useCallback(() => setJoinedRefreshKey((k) => k + 1), [])

    return (
        <TeamContext.Provider value={{ ownTeam, joinedTeam, mutateOwnTeam, mutateJoinedTeam }}>
            {children}
        </TeamContext.Provider>
    )
}

export const useTeamContext = () => useContext(TeamContext)
